/**
 * Strips Python-repr quote artifacts from a value returned by the SAMS API.
 *
 * Handles:
 *  - null / undefined  → ''
 *  - number / boolean  → String(value)
 *  - string[]          → recursively cleans each element, returns string[]
 *  - string            → unescapes \' / \", parses Python list-repr into string[],
 *                        strips a single matched pair of wrapping ' or " quotes,
 *                        preserves internal apostrophes (e.g. O'Brien)
 *
 * The function is idempotent: stripQuoteArtifacts(stripQuoteArtifacts(x)) === stripQuoteArtifacts(x).
 */
export function stripQuoteArtifacts(value: unknown): string | string[] {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return (value as unknown[]).map((item) => {
      const cleaned = stripQuoteArtifacts(item);
      // Elements are always strings in this context; join arrays that come back
      return Array.isArray(cleaned) ? cleaned.join(', ') : cleaned;
    });
  }

  if (typeof value === 'string') {
    // Step 1: unescape backslash-escaped quotes
    let result = value.replace(/\\'/g, "'").replace(/\\"/g, '"');

    // Step 2: detect Python list-repr and parse into array
    const trimmed = result.trim();
    if (/^\[.*\]$/.test(trimmed)) {
      try {
        const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
        if (Array.isArray(parsed)) {
          return parsed.map((item: unknown) => {
            const cleaned = stripQuoteArtifacts(item);
            return Array.isArray(cleaned) ? cleaned.join(', ') : cleaned;
          });
        }
      } catch {
        // Not valid JSON after substitution — fall through to scalar handling
      }
    }

    // Step 2b: detect single-quoted CSV without brackets — 'item1','item2',...
    if (/^'[^']*'(?:\s*,\s*'[^']*')+$/.test(result)) {
      const matches = result.match(/'[^']*'/g);
      if (matches) return matches.map((item) => item.slice(1, item.length - 1));
    }

    // Step 2c: detect double-quoted CSV without brackets — "item1","item2",...
    // Also handles one outer JSON-encoding layer: ""item1","item2","" → after Step 1
    // the escapes become regular quotes, leaving a double outer-quote pair.
    // Split on "," then strip any residual leading/trailing " from edge items.
    if (
      result.length >= 4 &&
      result[0] === '"' &&
      result[result.length - 1] === '"' &&
      result.includes('","')
    ) {
      const parts = result.slice(1, result.length - 1).split('","');
      if (parts.length > 1) {
        return parts.map((p) => p.replace(/^"|"$/g, ''));
      }
    }

    // Step 3: strip a single matched pair of wrapping quotes (' or ")
    // Guard: don't strip if the inner content looks like a quoted CSV
    // (same quote character adjacent to a comma), which would indicate the
    // outer quotes are item delimiters rather than a wrapper.
    if (
      result.length >= 2 &&
      ((result[0] === "'" && result[result.length - 1] === "'") ||
        (result[0] === '"' && result[result.length - 1] === '"'))
    ) {
      const q = result[0];
      const inner = result.slice(1, result.length - 1);
      const looksLikeCsv = inner.includes(`${q},`) || inner.includes(`,${q}`);
      if (!looksLikeCsv) {
        result = inner;
      }
    }

    // Step 4: strip any remaining stray backslash before a single quote
    result = result.replace(/\\(?=')/g, '');

    return result;
  }

  // Fallback for any other type
  return String(value);
}

const FORMAT_MAP: Record<string, Intl.DateTimeFormatOptions> = {
  'MM/DD/YYYY': { month: '2-digit', day: '2-digit', year: 'numeric' },
  'MM/DD/YYYY HH:mm A': { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true },
  'ddd MMM DD, YYYY': { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' },
};

/**
 * Formats an Intl.DateTimeFormat result to match moment.js punctuation conventions:
 * - 'MM/DD/YYYY HH:mm A': removes the comma between date and time (Intl emits "MM/DD/YYYY, HH:MM AM")
 * - 'ddd MMM DD, YYYY': removes the comma after the weekday (Intl emits "Ddd, Mon DD, YYYY")
 */
const applyFormatFix = (raw: string, format: string): string => {
  if (format === 'MM/DD/YYYY HH:mm A') {
    // Intl produces "06/03/2026, 02:30 PM" — remove the comma after the date portion
    return raw.replace(/^(\d{2}\/\d{2}\/\d{4}),\s*/, '$1 ');
  }
  if (format === 'ddd MMM DD, YYYY') {
    // Intl produces "Wed, Jun 03, 2026" — remove the comma after the weekday
    return raw.replace(/^(\w{3}),\s*/, '$1 ');
  }
  return raw;
};

export const isISO8601Date = (str: string): boolean =>
  /^\d{4}-\d{2}-\d{2}/.test(str) && !isNaN(new Date(str).getTime());

/**
 * Formats an ISO 8601 date string using the given format key.
 * Returns the raw value unchanged if the date is invalid or the format is unknown.
 * In development, logs a warning for unknown format keys.
 */
export const formatDate = (value: string, format: string): string => {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const options = FORMAT_MAP[format];
  if (!options) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`formatDate: unknown format key "${format}"`);
    }
    return value;
  }
  const raw = new Intl.DateTimeFormat('en-US', options).format(date);
  return applyFormatFix(raw, format);
};

/**
 * Formats a Unix timestamp (seconds or milliseconds) using the given format key.
 * Auto-detects ms vs seconds: values > 1e12 are treated as milliseconds, otherwise seconds.
 * Returns String(value) if the date is invalid or the format is unknown.
 * In development, logs a warning for unknown format keys.
 */
export const formatUnixDate = (value: number, format: string): string => {
  const ms = value > 1e12 ? value : value * 1000;
  const date = new Date(ms);
  if (isNaN(date.getTime())) return String(value);
  const options = FORMAT_MAP[format];
  if (!options) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`formatUnixDate: unknown format key "${format}"`);
    }
    return String(value);
  }
  const raw = new Intl.DateTimeFormat('en-US', options).format(date);
  return applyFormatFix(raw, format);
};

export const transformToMapsLink = (location: string): string => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
}

export const capitalizeFirstLetter = (string: string): string => {
  return string[0].toUpperCase() + string.slice(1, string.length);
}

export const lowercaseFirstLetter = (string: string): string => {
  return string[0].toLowerCase() + string.slice(1, string.length);
}

export const lowerCaseByProperty = (form: any, key: string): any => {
  return { ...form, [key]: form?.[key]?.toLowerCase() };
}
